import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { cn } from './utils';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

export interface AlertProps {
  variant: AlertVariant;
  children: ReactNode;
  title?: ReactNode;
  icon?: boolean | ReactNode;
  className?: string;
  onDismiss?: () => void;
}

const variantStyles: Record<AlertVariant, string> = {
  error: 'bg-error-bg border-error-border text-error-fg',
  success: 'bg-success-bg border-success-border text-success-fg',
  warning: 'bg-warning-bg border-warning-border text-warning-fg',
  info: 'bg-info-bg border-info-border text-info-fg',
};

const variantIcons: Record<AlertVariant, IconName> = {
  error: 'CircleAlert',
  success: 'CircleCheck',
  warning: 'TriangleAlert',
  info: 'Info',
};

const baseStyles = 'p-4 border rounded-lg text-sm';

export function Alert({
  variant,
  children,
  title,
  icon = false,
  className = '',
  onDismiss,
}: AlertProps) {
  const combinedClassName = cn(baseStyles, variantStyles[variant], className);

  const iconNode =
    icon === true ? <Icon name={variantIcons[variant]} size="md" /> : icon || null;

  return (
    <div className={combinedClassName} role="alert">
      <div className="flex items-start gap-3">
        {iconNode && (
          <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
            {iconNode}
          </span>
        )}
        <div className="flex-1 min-w-0">
          {title && <div className="font-medium mb-1">{title}</div>}
          <div>{children}</div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
